import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import Editor from './common/Editor';
import RenderCustomQuestions from './RenderCustomQuestions';
import CustomQuestionsManager from './CustomQuestionsManager';
import Link from 'next/link';
import { useSession } from './SessionContext';

type Props = {
    onClose: () => void;
    onSuccess: () => void;
    open: boolean;
    job?: {
        _id: string,
        status: string,
        title: string,
        description: string,
        recruiterEmailAddress: string;
        locationType: string;
        location: string;
        minSalary: string;
        maxSalary: string;
        customQuestionIds?: string[];
    }
};

const JobForm = ({ open, onClose, onSuccess, job }: Props) => {
    const { user } = useSession();

    const jobO = job || { _id: null, status: '', title: '', description: '', recruiterEmailAddress: '', locationType: '', location: '', maxSalary: '', minSalary: '' };

    const [title, setTitle] = useState(jobO.title);
    const [description, setDescription] = useState(jobO.description);
    const [locationType, setLocationType] = useState(jobO.locationType);
    const [location, setLocation] = useState(jobO.location);
    const [minSalary, setMinSalary] = useState(jobO.minSalary);
    const [maxSalary, setMaxSalary] = useState(jobO.maxSalary);
    const [recruiterEmailAddress, setRecruiterEmailAddress] = useState(jobO.recruiterEmailAddress || user.email);
    const [saving, setSaving] = useState(false);
    const [customQuestions, setCustomQuestions] = useState([]);
    const [selectedCustomQuestionId, setSelectedCustomQuestionId] = useState();
    const [customQuestionIds, setCustomQuestionIds] = useState(jobO.customQuestionIds || []);
    const [showCustomQuestionsManager, setShowCustomQuestionsManager] = useState(false);

    const [isDeleting, setIsDeleting] = useState(false);
    const [applicantsCountForDeleting, setApplicantsCountForDeleting] = useState(0);

    const getCustomQuestions = () => {
        sendApiRequest({
            path: `/custom-questions`,
            method: 'GET',
        }).then((response: any) => {
            if (!response.error) {
                setCustomQuestions(response);
            }
        }).catch((error) => {
            setCustomQuestions([]);
            notify(error);
        });
    }

    useEffect(() => {
        getCustomQuestions();
    }, []);

    const onSelectCustomQuestion = (e) => {
        const id = e.target.value;

        setSelectedCustomQuestionId(id);

        if (id && !customQuestionIds.includes(id)) {
            customQuestionIds.push(id);
            setCustomQuestionIds([...customQuestionIds]);
        }
    }

    const onSave = async () => {
        setSaving(true);

        try {
            await sendApiRequest({
                path: '/jobs/create-or-update',
                method: 'POST',
                data: {
                    _id: jobO._id,
                    title,
                    description,
                    locationType,
                    location,
                    recruiterEmailAddress,
                    minSalary,
                    maxSalary,
                    customQuestionIds
                }
            });

            onSuccess();

            setSaving(false);
        } catch (error) {
            setSaving(false);
            notify(error);
        }

    };

    const checkApplicantsCountForDeleting = async () => {
        setIsDeleting(true);

        try {
            const { applicantsCount } = await sendApiRequest({
                path: '/jobs/delete',
                method: 'POST',
                data: {
                    _id: jobO._id,
                    action: 'check',
                }
            });

            if (applicantsCount > 0) {
                setApplicantsCountForDeleting(applicantsCount);
                setIsDeleting(false);
            } else {
                await deleteJob();
            }
        } catch (error) {
            setIsDeleting(false);
            notify(error);
        }
    };

    const deleteJob = async () => {
        setIsDeleting(true);

        try {
            await sendApiRequest({
                path: '/jobs/delete',
                method: 'POST',
                data: {
                    _id: jobO._id,
                }
            });

            onSuccess();
            setIsDeleting(false);
        } catch (error) {
            setIsDeleting(false);
            notify(error);
        }
    };

    const onChangeStatus = async (e) => {
        setSaving(true);

        try {
            await sendApiRequest({
                path: '/jobs/update-field',
                method: 'POST',
                data: {
                    _id: jobO._id,
                    fieldName: 'status',
                    value: e.target.value,
                }
            });

            onSuccess();

            notify({ message: 'Changed' });

            setSaving(false);
        } catch (error) {
            notify(error);
        }
    };

    const renderDeleteConfirmation = () => {
        if (applicantsCountForDeleting === 0) {
            return null;
        }

        return (
            <Dialog onClose={() => { setApplicantsCountForDeleting(0); setIsDeleting(false); } } aria-labelledby="modal-title" open={true} maxWidth='md'>
                <DialogTitle style={{ fontWeight: 'bold' }}>Are you sure?</DialogTitle>

                <DialogContent>
                    <p>Deleting job will result in deletion of <b>{applicantsCountForDeleting}</b> job applicant{applicantsCountForDeleting > 1 ? 's' : ''}.</p>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => { setApplicantsCountForDeleting(0); setIsDeleting(false); } } variant="contained" color="inherit">Cancel</Button>
                    <Button onClick={deleteJob} variant="contained" color="error" disabled={isDeleting}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Dialog onClose={onClose} aria-labelledby="modal-title" open={open} fullScreen style={{ width: '60%', left: 'inherit' }}>
            <DialogTitle id="modal-title">
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1} >{ jobO.title ? 'Edit job posting' : 'Create a new job posting'}</Box>
                    <Box>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>

                        <Button variant="contained" color="success" onClick={onSave} disabled={saving}>
                            Save
                        </Button>

                        {
                            jobO._id ?
                            (
                                <>
                                    <Select className='status-chooser' defaultValue={job.status} onChange={onChangeStatus}>
                                        <MenuItem value="open"><span className='circle-icon open' /> Open</MenuItem>
                                        <MenuItem value="closed"><span className='circle-icon closed' /> Closed</MenuItem>
                                        <MenuItem value="draft"><span className='circle-icon draft' /> Draft</MenuItem>
                                    </Select>

                                    <Button variant="contained" color="error" onClick={checkApplicantsCountForDeleting} disabled={isDeleting} style={{ marginLeft: '5px'}}>
                                        Delete
                                    </Button>
                                </>
                            ) : null
                        }
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent style={{ backgroundColor: '#f8f8f8' }}>
                <br />

                {renderDeleteConfirmation()}

                <div>
                    <FormControl fullWidth variant="outlined" required>
                        <FormLabel>Job title</FormLabel>
                        <TextField value={title} onChange={(e) => setTitle(e.target.value)} />
                    </FormControl>
                </div>
                <br />

                <div>
                    <FormControl fullWidth variant="outlined" required>
                        <FormLabel>Location type</FormLabel>
                        <RadioGroup
                            row
                            defaultValue="onSite"
                            value={locationType}
                            name="radio-buttons-group"
                            onChange={(e) => { setLocationType(e.target.defaultValue) }}
                        >
                            <FormControlLabel value="OnSite" control={<Radio />} label="On-site" />
                            <FormControlLabel value="Hybrid" control={<Radio />} label="Hybrid" />
                            <FormControlLabel value="Remote" control={<Radio />} label="Remote" />
                        </RadioGroup>
                    </FormControl>
                </div>
                <br />

                <div>
                    <FormControl fullWidth variant="outlined" required>
                        <FormLabel>Location</FormLabel>
                        <TextField value={location} onChange={(e) => setLocation(e.target.value)} />
                    </FormControl>
                </div>
                <br />

                <div>
                    <FormControl fullWidth variant="outlined" required>
                        <h2>Job description</h2>
                        <Editor style={{ height: '300px' }} value={description} onChange={(value) => setDescription(value)} />
                    </FormControl>
                </div>
                <br />
                <br />

                <div>
                    <h2>Salary range</h2>

                    <div className='salary-range'>
                        <label>Minimum</label>
                        <TextField value={minSalary} type='number' onChange={(e) => setMinSalary(e.target.value)} />

                        <span>USD</span>
                    </div>

                    <div className='salary-range'>
                        <label>Maximum</label>
                        <TextField value={maxSalary} type='number' onChange={(e) => setMaxSalary(e.target.value)} />

                        <span>USD</span>
                    </div>
                </div>

                <div style={{ clear: 'both' }} />
                <br />

                <div>
                    <FormControl fullWidth variant="outlined" required>
                        <FormLabel>Recruiter email address</FormLabel>
                        <TextField value={recruiterEmailAddress} onChange={(e) => setRecruiterEmailAddress(e.target.value)} />
                    </FormControl>
                </div>

                <div>
                    <h2>Custom application questions</h2>

                    {
                        showCustomQuestionsManager && (
                            <CustomQuestionsManager
                                onClose={() => { setShowCustomQuestionsManager(false); getCustomQuestions(); } }
                            />
                        )
                    }

                    <Link href="#" onClick={() => setShowCustomQuestionsManager(true)} style={{ display: 'block', marginBottom: '40px', marginTop: '-10px', color: 'red', textDecoration: 'underline' }}>
                        Manage custom applications
                    </Link>

                    <RenderCustomQuestions
                        questions={customQuestions.filter(cq => customQuestionIds.includes(cq._id))}
                        onDelete={(questionId) => {
                            const newCustomQuestionIds = customQuestionIds.filter(id => id !== questionId);
                            setCustomQuestionIds([...newCustomQuestionIds]);
                            setSelectedCustomQuestionId(null);
                        }}
                    />

                    <Select onChange={onSelectCustomQuestion} style={{ width: '300px', marginRight: '20px', padding: '0p' }} value={selectedCustomQuestionId || ''}>
                        {customQuestions.filter(cq => !customQuestionIds.includes(cq._id)).map((question) => {
                            return (
                                <MenuItem key={question._id} value={question._id}>{question.name}</MenuItem>
                            )
                        })}
                    </Select>
                </div>
                <br />
                <br />
            </DialogContent>
        </Dialog>
    );
};

export default JobForm;
